<template>
  <AppLayout :breadcrumbs="[{ label: 'Products', link: '/products' }]">
    <div class="layout-header">
      <div class="header-left">
        <SearchBar v-model="searchTerm" @update:modelValue="handleSearch" placeholder="Search products..." />
      </div>
      <div class="header-right">
        <button class="button icon-button" @click="openColumnSettingsModal" title="Column Settings">
          <CogIcon :size="18" />
        </button>
        <button class="button" @click="openExportModal" :disabled="totalProducts === 0">
          Export
        </button>
        <button v-if="currentUser && currentUser.isAdmin" class="button primary" @click="handleCreateProduct">
          Create Product
        </button>
      </div>
    </div>

    <div class="layout-body">
      <CustomTable :columns="visibleColumnsFiltered" :rows="products" :sticky-first-column="true" :loading="isLoading"
        :is-empty="products.length === 0" :sort-by="sortBy" :sort-order="sortOrder" :row-class="getRowClass" @row-click="handleRowClick" @sort="handleSort">
        <template #column-productName="{ row }">
          <div class="product-name-cell">
            <IconCircle :size="28" bgColor="var(--icon-product-bg)" color="var(--icon-product-color)" :icon="getProductIcon(row)" />
            <span class="name-text">{{ row.name }}</span>
          </div>
        </template>

        <template #column-productType="{ row }">
          <span v-if="row.productType" class="cell-value">{{ formatProductType(row.productType) }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-defaultQuantity="{ row }">
          <span v-if="row.defaultQuantity !== null" class="cell-value">{{ row.defaultQuantity }}</span>
          <span v-else class="no-data">No default</span>
        </template>

        <template #column-defaultUnitPrice="{ row }">
          <span v-if="row.defaultUnitPriceCents !== null" class="cell-value">
            {{ formatUtils.formatCurrency(row.defaultUnitPriceCents) }}
          </span>
          <span v-else class="no-data">No default</span>
        </template>

        <template #column-defaultDuration="{ row }">
          <span v-if="row.defaultDurationMonths !== null" class="cell-value">
            {{ row.defaultDurationMonths }} {{ row.defaultDurationMonths === 1 ? 'month' : 'months' }}
          </span>
          <span v-else class="no-data">No default</span>
        </template>

        <template #column-allowanceEnabled="{ row }">
          <span class="cell-value">{{ row.allowanceEnabled ? 'Yes' : 'No' }}</span>
        </template>

        <template #column-allowanceUnit="{ row }">
          <span v-if="row.allowanceUnit" class="cell-value">{{ row.allowanceUnit }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-allowanceAmount="{ row }">
          <span v-if="row.allowanceAmount !== null" class="cell-value">{{ row.allowanceAmount }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-description="{ row }">
          <span v-if="row.description" class="cell-value cell-truncate">{{ row.description }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-insuranceCodes="{ row }">
          <span v-if="row.insuranceCodes" class="cell-value cell-truncate">{{ row.insuranceCodes }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-diagnosisCodes="{ row }">
          <span v-if="row.diagnosisCodes" class="cell-value cell-truncate">{{ row.diagnosisCodes }}</span>
          <span v-else class="no-data">No data</span>
        </template>

      </CustomTable>
    </div>

    <div class="layout-footer">
      <PaginationControl :current-page="currentPage" :total-items="totalProducts" :items-per-page="itemsPerPage"
        @page-change="handlePageChange" />
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <ExportDataModal
              v-if="modal.id === 'export'"
              entity-type="products"
              :columns="columns"
              :current-page-count="products.length"
              :total-count="totalProducts"
              :export-config="exportConfig"
              :is-exporting="isExporting"
              :export-progress="exportProgress"
              :error="exportError"
              @export="handleExport"
            />
            <ColumnSettingsModal
              v-else-if="modal.id === 'columnSettings'"
              :columns="columns"
              :storage-key="'shawncrm_columns_products'"
              :initial-visibility="columnVisibility"
              title="Table Settings"
              subtitle="Configure what you see in the products table"
              @update:visibility="handleColumnVisibilityChange"
            >
              <template #extra>
                <div class="extra-toggle-row">
                  <div class="extra-toggle-info">
                    <span class="extra-toggle-label">Show deactivated products</span>
                    <span class="extra-toggle-hint">Deactivated products appear at the bottom of the table.</span>
                  </div>
                  <SwitchInput :modelValue="showDeactivated" @update:modelValue="handleToggleDeactivated" />
                </div>
              </template>
            </ColumnSettingsModal>
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '../../components/app/AppLayout.vue';
import SearchBar from '../../components/common/SearchBar.vue';
import CustomTable from '../../components/common/CustomTable.vue';
import PaginationControl from '../../components/common/PaginationControl.vue';
import IconCircle from '@/components/common/IconCircle.vue';
import ExportDataModal from '../../modals/ExportDataModal.vue';
import ColumnSettingsModal from '../../modals/ColumnSettingsModal.vue';
import SwitchInput from '@/components/inputs/SwitchInput.vue';
import PillIcon from 'vue-material-design-icons/Pill.vue';
import CurrencyUsdIcon from 'vue-material-design-icons/CurrencyUsd.vue';
import PackageIcon from 'vue-material-design-icons/Package.vue';
import CogIcon from 'vue-material-design-icons/Cog.vue';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { generateCSV, downloadCSV } from '@/utils/csvExport';
import { getLocalToday } from '@/utils/dateUtils';

export default {
  name: 'ProductListView',
  data() {
    return {
      searchTerm: '',
      itemsPerPage: 100,
      currentPage: 1,
      totalProducts: 0,
      products: [],
      isLoading: false,
      currentUser: null,
      sortBy: 'name',
      sortOrder: 'asc',
      showDeactivated: false,
      columnVisibility: {},
      // Modal state
      modal: {
        open: false,
        id: '',
        data: {}
      },
      // Export state
      isExporting: false,
      exportProgress: { current: 0, total: 0 },
      exportError: null,
      columns: [
        { key: 'productName', label: 'Product Name', width: '300px', sortable: true, sortKey: 'name' },
        { key: 'productType', label: 'Type', width: '200px', sortable: true, sortKey: 'productType' },
        { key: 'defaultQuantity', label: 'Default Qty', width: '200px', description: 'Default quantity when adding this product to a ticket' },
        { key: 'defaultUnitPrice', label: 'Unit Price', width: '200px', sortable: true, sortKey: 'defaultUnitPriceCents', description: 'Default price per unit when adding this product to a ticket' },
        { key: 'defaultDuration', label: 'Default Duration', width: '200px', sortable: true, sortKey: 'defaultDurationMonths', description: 'Default payment plan duration in months for this product' },
        { key: 'allowanceEnabled', label: 'Allowance', width: '150px', description: 'Whether allowance tracking is enabled for this product' },
        { key: 'allowanceUnit', label: 'Allowance Unit', width: '180px', description: 'Unit of measurement for allowance tracking (e.g. tablets, doses)' },
        { key: 'allowanceAmount', label: 'Allowance Amt', width: '180px', description: 'Allowance amount per unit quantity' },
        { key: 'description', label: 'Description', width: '200px', description: 'Product description' },
        { key: 'insuranceCodes', label: 'Insurance Codes', width: '200px', description: 'Insurance codes for this product' },
        { key: 'diagnosisCodes', label: 'Diagnosis Codes', width: '200px', description: 'Diagnosis codes for this product' }
      ],
      // Export column mapping
      exportConfig: {
        productName: {
          label: 'Product Name',
          format: (row) => row.name || ''
        },
        productType: {
          label: 'Product Type',
          format: (row) => {
            if (!row.productType) return '';
            const typeMap = {
              'medication': 'Medication',
              'fee': 'Fee',
              'other': 'Other'
            };
            return typeMap[row.productType] || row.productType;
          }
        },
        defaultQuantity: {
          label: 'Quantity',
          format: (row) => row.defaultQuantity !== null ? String(row.defaultQuantity) : ''
        },
        defaultUnitPrice: {
          label: 'Unit Price',
          format: (row) => row.defaultUnitPriceCents !== null ? formatUtils.formatCurrency(row.defaultUnitPriceCents) : ''
        },
        defaultDuration: {
          label: 'Duration',
          format: (row) => {
            if (row.defaultDurationMonths === null) return '';
            return `${row.defaultDurationMonths} ${row.defaultDurationMonths === 1 ? 'month' : 'months'}`;
          }
        },
        allowanceEnabled: {
          label: 'Allowance Enabled',
          format: (row) => row.allowanceEnabled ? 'Yes' : 'No'
        },
        allowanceUnit: {
          label: 'Allowance Unit',
          format: (row) => row.allowanceUnit || ''
        },
        allowanceAmount: {
          label: 'Allowance Amount',
          format: (row) => row.allowanceAmount !== null ? String(row.allowanceAmount) : ''
        },
        description: {
          label: 'Description',
          format: (row) => row.description || ''
        },
        insuranceCodes: {
          label: 'Insurance Codes',
          format: (row) => row.insuranceCodes || ''
        },
        diagnosisCodes: {
          label: 'Diagnosis Codes',
          format: (row) => row.diagnosisCodes || ''
        }
      },
      formatUtils
    };
  },
  computed: {
    visibleColumnsFiltered() {
      return this.columns.filter(col => {
        // Always show first column (productName)
        if (col.key === 'productName') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.columnVisibility[col.key] !== false;
      });
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return;

        const urlSearch = newQuery.search || '';
        const urlPage = parseInt(newQuery.page) || 1;
        const urlSortBy = newQuery.sortBy || 'name';
        const urlSortOrder = newQuery.sortOrder || 'asc';

        const hasChanges =
          urlSearch !== this.searchTerm ||
          urlPage !== this.currentPage ||
          urlSortBy !== this.sortBy ||
          urlSortOrder !== this.sortOrder;

        if (!hasChanges) return;

        this.searchTerm = urlSearch;
        this.currentPage = urlPage;
        this.sortBy = urlSortBy;
        this.sortOrder = urlSortOrder;

        this.loadProducts();
      }
    }
  },
  created() {
    // Check if URL has any state params
    const hasUrlParams =
      this.$route.query.search !== undefined ||
      this.$route.query.sortBy !== undefined ||
      this.$route.query.sortOrder !== undefined;

    if (hasUrlParams) {
      // Load from URL (existing logic)
      if (this.$route.query.search !== undefined) {
        this.searchTerm = this.$route.query.search || '';
      }
      if (this.$route.query.sortBy !== undefined) {
        this.sortBy = this.$route.query.sortBy || 'name';
      }
      if (this.$route.query.sortOrder !== undefined) {
        this.sortOrder = this.$route.query.sortOrder || 'asc';
      }
    } else {
      // Load from localStorage
      const savedState = this.loadFiltersFromLocalStorage();
      if (savedState) {
        this.sortBy = savedState.sortBy || 'name';
        this.sortOrder = savedState.sortOrder || 'asc';

        // Immediately sync to URL
        this.syncStateToUrl();
      }
    }

    // showDeactivated is a client-only preference — always load from localStorage
    const savedShowDeactivated = localStorage.getItem('shawncrm_showDeactivated_products');
    this.showDeactivated = savedShowDeactivated === 'true';

    this.currentPage = parseInt(this.$route.query.page) || 1;

    // Load column visibility settings
    this.loadColumnVisibility();

    this.currentUser = queryClient.getQueryData(['user']) || null;
    this.loadProducts();
  },
  methods: {
    syncStateToUrl() {
      const query = {};

      if (this.searchTerm) {
        query.search = this.searchTerm;
      }

      if (this.currentPage > 1) {
        query.page = this.currentPage;
      }

      if (this.sortBy !== 'name') {
        query.sortBy = this.sortBy;
      }

      if (this.sortOrder !== 'asc') {
        query.sortOrder = this.sortOrder;
      }

      this.$router.replace({ query }).catch(() => {});
    },
    async loadProducts() {
      try {
        this.isLoading = true;

        const offset = (this.currentPage - 1) * this.itemsPerPage;
        const response = await queryClient.fetchQuery({
          queryKey: ['products', {
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            includeDeleted: this.showDeactivated
          }],
          queryFn: () => api.fetchProducts({
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            includeDeleted: this.showDeactivated
          })
        });

        this.products = response.products;
        this.totalProducts = response.total;

      } catch (err) {
        console.error('Failed to fetch products:', err);
        alertService.error('Failed to load products: ' + err.message);
        this.products = [];
        this.totalProducts = 0;
      } finally {
        this.isLoading = false;
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadProducts();
    },
    handleSort({ sortBy, sortOrder }) {
      this.sortBy = sortBy;
      this.sortOrder = sortOrder;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadProducts();
    },
    handleToggleDeactivated(value) {
      this.showDeactivated = value;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.loadProducts();
    },
    getRowClass(row) {
      return row.isDeleted ? 'deactivated-row' : '';
    },
    saveFiltersToLocalStorage() {
      try {
        // Store sort settings
        localStorage.setItem('shawncrm_sortBy_products', this.sortBy);
        localStorage.setItem('shawncrm_sortOrder_products', this.sortOrder);
        localStorage.setItem('shawncrm_showDeactivated_products', this.showDeactivated ? 'true' : 'false');
      } catch (err) {
        console.error('Failed to save filters to localStorage:', err);
      }
    },
    loadFiltersFromLocalStorage() {
      try {
        const sortBy = localStorage.getItem('shawncrm_sortBy_products');
        const sortOrder = localStorage.getItem('shawncrm_sortOrder_products');
        const showDeactivated = localStorage.getItem('shawncrm_showDeactivated_products');

        return {
          sortBy: sortBy || 'name',
          sortOrder: sortOrder || 'asc',
          showDeactivated: showDeactivated === 'true'
        };
      } catch (err) {
        console.error('Failed to load filters from localStorage:', err);
        return null;
      }
    },
    handleCreateProduct() {
      this.$router.push('/products/new');
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.syncStateToUrl();
      this.loadProducts();
    },
    handleRowClick({ rowIndex }) {
      const product = this.products[rowIndex];
      if (product && product.productId) {
        this.$router.push(`/products/${product.productId}`);
      }
    },
    getProductIcon(product) {
      switch (product.productType) {
        case 'medication':
          return PillIcon;
        case 'fee':
          return CurrencyUsdIcon;
        case 'other':
        default:
          return PackageIcon;
      }
    },
    formatProductType(value) {
      const typeMap = {
        'medication': 'Medication',
        'fee': 'Fee',
        'other': 'Other'
      };
      return typeMap[value] || value;
    },
    loadColumnVisibility() {
      try {
        const saved = localStorage.getItem('shawncrm_columns_products');
        if (saved) {
          this.columnVisibility = JSON.parse(saved);
        } else {
          // Initialize all columns as visible by default
          this.columns.forEach(col => {
            this.columnVisibility[col.key] = true;
          });
        }
      } catch (err) {
        console.error('Failed to load column visibility:', err);
        // Initialize all columns as visible on error
        this.columns.forEach(col => {
          this.columnVisibility[col.key] = true;
        });
      }
    },
    saveColumnVisibility() {
      try {
        localStorage.setItem(
          'shawncrm_columns_products',
          JSON.stringify(this.columnVisibility)
        );
      } catch (err) {
        console.error('Failed to save column visibility:', err);
      }
    },
    handleColumnVisibilityChange(visibility) {
      this.columnVisibility = visibility;
      this.saveColumnVisibility();
    },
    // Modal management
    openColumnSettingsModal() {
      this.openModal('columnSettings', {});
    },
    openExportModal() {
      this.openModal('export', {});
    },
    openModal(id, data) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
        this.modal.data = data;
      });
    },
    closeModal() {
      this.modal.id = '';
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      const classList = event.target.classList;
      if (classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    // Export modal methods
    async handleExport({ scope, columns }) {
      this.isExporting = true;
      this.exportError = null;
      this.exportProgress = { current: 0, total: 0 };

      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        let dataToExport = [];

        if (scope === 'current') {
          dataToExport = this.products;
          this.exportProgress = { current: this.products.length, total: this.products.length };
        } else {
          const filterParams = this.getFilterParams();
          const pageSize = 100;

          const firstPageResponse = await api.fetchProducts({
            limit: pageSize,
            offset: 0,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            includeDeleted: this.showDeactivated,
            ...filterParams
          });

          const totalRecords = firstPageResponse.total;
          const totalPages = Math.ceil(totalRecords / pageSize);

          this.exportProgress = { current: 0, total: totalRecords };
          await this.$nextTick();

          dataToExport = [...firstPageResponse.products];
          this.exportProgress.current = firstPageResponse.products.length;
          await this.$nextTick();

          for (let page = 1; page < totalPages; page++) {
            await new Promise(resolve => setTimeout(resolve, 100));

            const offset = page * pageSize;
            const response = await api.fetchProducts({
              limit: pageSize,
              offset,
              search: this.searchTerm,
              sortBy: this.sortBy,
              sortOrder: this.sortOrder,
              ...filterParams
            });

            dataToExport = [...dataToExport, ...response.products];
            this.exportProgress.current = dataToExport.length;
            await this.$nextTick();
          }
        }

        const csvContent = generateCSV(dataToExport, columns, this.exportConfig);

        const timestamp = getLocalToday();
        const filename = `products-export-${timestamp}`;
        downloadCSV(csvContent, filename);

        alertService.success(`Successfully exported ${dataToExport.length} products`);

        // Close modal after short delay to show completion
        setTimeout(() => {
          this.closeModal();
          this.isExporting = false;
          this.exportProgress = { current: 0, total: 0 };
        }, 500);

      } catch (err) {
        console.error('Export failed:', err);
        this.exportError = err.message || 'Failed to export data. Please try again.';
        this.isExporting = false;
      }
    }
  },
  components: {
    AppLayout,
    SearchBar,
    CustomTable,
    PaginationControl,
    IconCircle,
    ExportDataModal,
    ColumnSettingsModal,
    CogIcon,
    SwitchInput
  }
}
</script>

<style scoped>
/* Header section */
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-left> :first-child {
  max-width: 300px;
  flex: 1;
  min-width: 200px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.layout-header .button {
  white-space: nowrap;
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  min-width: 32px;
}

/* Product name cell styling */
.product-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.icon-circle {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  background-color: #e1edff;
  color: #406396;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.icon-text {
  font-size: 12px;
  font-weight: 500;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Cell value styling */
.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.no-data {
  color: #9ca3af;
  font-style: italic;
}

.cell-truncate {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
}

.extra-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.extra-toggle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.extra-toggle-label {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.extra-toggle-hint {
  font-size: 12px;
  color: #6b7280;
}

:deep(.data-row.deactivated-row) {
  background-color: #fafafa;
}

:deep(.data-row.deactivated-row) td {
  color: #9ca3af;
  font-style: italic;
}

:deep(.data-row.deactivated-row) .name-text {
  color: #6b7280;
  text-decoration: line-through;
}

:deep(.data-row.deactivated-row) .icon-circle {
  opacity: 0.5;
}
</style>
